import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';

import { IconButtonComponent, trackByFactory } from '@mode/capra';
import { GoogleSheetUploadFacade } from '@mode/dataset/google/contract';
import { PAGE_CONTEXT } from '@mode/shared/util-segment';
import { FeatureFlag, ReportTypes } from '@mode/shared/contract-common';
import { getInitialFlagValue, LocalStorageService, MetaTagsService } from '@mode/shared/util-js';
import { ReportCreationMenuItemComponent } from '../report-creation-menu-item/report-creation-menu-item.component';
import {
  existingDataOption,
  reusableDatasetOption,
  uploadExcelSheetOption,
  uploadSheetOption,
  writeSqlOption,
} from '@mode/shared/ui';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { AnalyticsEvent } from '@mode/shared/util-segment';

@Component({
  standalone: true,
  selector: 'mode-report-creation-menu',
  imports: [CommonModule, IconButtonComponent, MatLegacyMenuModule, ReportCreationMenuItemComponent],
  templateUrl: './report-creation-menu.component.html',
  styleUrls: ['./report-creation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportCreationMenuComponent implements OnInit {
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() org = '';
  menuItems: ReportTypes.ReportCreationMenuItem[] = [];
  optionCategoryRecord: Record<string, 'reporting' | 'datasets'> | undefined = {
    'create-new-report-with-existing-data': 'reporting',
    'create-new-report-with-sql': 'reporting',
    'create-new-dataset': 'datasets',
    'upload-new-sheet': 'datasets',
    'upload-new-excel-sheet': 'datasets',
  };
  isAnalystStudio$ = this.thoughtspotIntegrationService.currOrgIntegrated$;
  enableExcelSheetUpload = getInitialFlagValue(FeatureFlag.ExcelSheetDatasetUpload);
  readonly trackByFn = trackByFactory<ReportTypes.ReportCreationMenuItem>(({ title }) => title);

  ngOnInit(): void {
    this.menuItems = [
      existingDataOption({
        location: this._pageContext,
        action: AnalyticsEvent.Name.UseExistingData,
      }),
      writeSqlOption(this.org, {
        location: this._pageContext,
        action: AnalyticsEvent.Name.NewQuery,
      }),
      uploadSheetOption(
        'Create a Dataset from an existing Worksheet',
        AnalyticsEvent.SubContext.GoogleSheetUploadPlusMenu,
        this.localStorageService,
        this.meta
      ),
      ...(this.enableExcelSheetUpload
        ? [
            uploadExcelSheetOption(
              'Create a Dataset from an existing Worksheet',
              AnalyticsEvent.SubContext.ExcelSheetUploadPlusMenu,
              this.localStorageService,
              this.meta
            ),
          ]
        : []),
      reusableDatasetOption(this.org, {
        location: this._pageContext,
        action: AnalyticsEvent.Name.NewDataset,
      }),
    ];
  }

  constructor(
    @Inject(PAGE_CONTEXT) private _pageContext: string,
    private _uploadSheetFacade: GoogleSheetUploadFacade,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService,
    private meta: MetaTagsService,
    private localStorageService: LocalStorageService
  ) {}

  async onClick(event: MouseEvent, testAttr: string) {
    if (testAttr === 'upload-new-sheet') {
      event.preventDefault();
      this._uploadSheetFacade.launchDialog();
    } else if (testAttr === 'upload-new-excel-sheet') {
      event.preventDefault();
      // TODO: Add Excel sheet upload
    }
  }
}
